/**
 * PWA Client Utilities
 * Handles service worker registration and install prompts
 */

/**
 * Register service worker for offline support
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    console.warn("[PWA] Service Worker not supported");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });

    console.log("[PWA] Service Worker registered:", registration.scope);

    // Handle updates
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            // New service worker available - prompt user to reload
            console.log("[PWA] New service worker available");
            if (confirm("تحديث جديد متاح. هل تريد إعادة تحميل الصفحة؟")) {
              window.location.reload();
            }
          }
        });
      }
    });

    return registration;
  } catch (error) {
    console.error("[PWA] Service Worker registration failed:", error);
    return null;
  }
}

/**
 * Check if PWA is installable and show install prompt
 */
export async function showInstallPrompt(): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  // Check if already installed
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return false; // Already installed
  }

  // Check for beforeinstallprompt event
  let deferredPrompt: any = null;

  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });

  if (deferredPrompt) {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    return outcome === "accepted";
  }

  return false;
}

/**
 * Check if app is running as PWA
 */
export function isPWAInstalled(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes("android-app://")
  );
}

/**
 * Initialize PWA features
 */
export async function initializePWA(): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }

  // Register service worker (existing)
  await registerServiceWorker();

  // Register Workbox service worker (enhanced)
  try {
    const { registerWorkboxServiceWorker } = await import("~/lib/offline/workbox.client");
    await registerWorkboxServiceWorker();
  } catch (error) {
    console.warn("[PWA] Workbox registration failed, using fallback:", error);
  }

  // Handle online/offline events
  window.addEventListener("online", () => {
    console.log("[PWA] Back online");
    // Trigger sync if available
    if ("serviceWorker" in navigator && "sync" in ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker.ready.then((registration: any) => {
        registration.sync.register("sync-transactions").catch(console.error);
      });
    }
  });

  window.addEventListener("offline", () => {
    console.log("[PWA] Gone offline");
  });
}
